/**
 * Shared HTML sanitization utilities.
 *
 * IMPORTANT:
 * - `dangerouslySetInnerHTML` should only be used with content passed through `sanitizeHtml`.
 * - Keep the allowlist tight; expand only when a real product requirement exists.
 */
import DOMPurify from 'dompurify';
 
const DEFAULT_ALLOWED_TAGS = [
  'p', 'br',
  'strong', 'b', 'em', 'i', 'u',
  'ul', 'ol', 'li',
  'a',
  'span', 'div',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
];
 
const DEFAULT_ALLOWED_ATTR = ['href', 'target', 'rel'];
 
// Allow only safe/expected URL schemes for links. Relative URLs are allowed.
const SAFE_URI_PATTERN = /^(?:(?:https?|mailto|tel):|\/|#)/i;
 
function normalizeAnchorAttributes(node) {
  // Ensure links opened in a new tab cannot control the opener (tabnabbing).
  if (!node || node.nodeName !== 'A') return;
 
  const target = (node.getAttribute('target') || '').trim().toLowerCase();
  if (target === '_blank') {
    const rel = (node.getAttribute('rel') || '').trim();
    const relTokens = new Set(rel.split(/\s+/).filter(Boolean).map((t) => t.toLowerCase()));
    relTokens.add('noopener');
    relTokens.add('noreferrer');
    node.setAttribute('rel', Array.from(relTokens).join(' '));
  }
}
 
/**
 * Sanitize HTML string for safe insertion into the DOM.
 * @param {string} html - Raw HTML (e.g. from API content)
 * @returns {string} Sanitized HTML safe for dangerouslySetInnerHTML
 */
export function sanitizeHtml(html) {
  if (html == null || typeof html !== 'string') return '';
 
  // Hooks are global per DOMPurify instance; ensure we don't attach duplicates.
  // eslint-disable-next-line no-underscore-dangle
  if (!DOMPurify.__NYAFAZ_HOOKS_ATTACHED__) {
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      normalizeAnchorAttributes(node);
 
      if (node && node.nodeName === 'A') {
        const href = (node.getAttribute('href') || '').trim();
        if (href && !SAFE_URI_PATTERN.test(href)) {
          node.removeAttribute('href');
        }
      }
    });
    // eslint-disable-next-line no-underscore-dangle
    DOMPurify.__NYAFAZ_HOOKS_ATTACHED__ = true;
  }
 
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: DEFAULT_ALLOWED_TAGS,
    ALLOWED_ATTR: DEFAULT_ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: SAFE_URI_PATTERN,
    // Defensive: never allow unknown protocols.
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });
}
 
/**
 * Strip HTML tags for plain-text display (e.g. content stored as <p>content</p>).
 * @param {string} html - String that may contain HTML
 * @returns {string} Plain text with tags removed
 */
export function stripHtml(html) {
  if (html == null || typeof html !== 'string') return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

